import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { EPath } from '@/router/path';
import { EToastType } from '@/stores/feedback';
import { useToast } from '@/hooks/useToast';
import { runErrorStrategy } from '@/utils/request/errorStrategy';
import { type AppErrorHandlers } from '@/utils/request/matchError';

export interface IErrorHandleOptions {
  /** 复用已有 toast 的 id,把 LOADING 原地替换成错误提示 */
  toastId?: string;
  /** 跳登录时携带的回跳地址,默认为当前路径 */
  redirect?: string;
}

/** 这些页面本身就是登录出入口,把它们当回跳地址会让登录成功后又回到登录页 */
const NO_REDIRECT_PATHS: ReadonlySet<string> = new Set<string>([EPath.Login, EPath.Logout]);

/** 当前路径不适合作为回跳地址时返回 undefined */
function currentRedirect(): string | undefined {
  // 不用 useLocation:那会让 handleError 每次路由变化都换身份,
  // 连带所有把它写进依赖数组的 useCallback 一起重建
  const { pathname } = window.location;
  return NO_REDIRECT_PATHS.has(pathname) ? undefined : pathname;
}

/**
 * 请求错误默认策略层的 React 接线,策略本身见 runErrorStrategy。
 *
 * 注意 401 的默认行为是跳登录 —— 编辑器内的调用点如果不希望把用户带离画布,
 * 需要显式覆盖 `EAppErrorKind.UNAUTHORIZED`。
 */
export const useErrorHandler = () => {
  const { t } = useTranslation();
  const { add: addToast } = useToast();
  const navigate = useNavigate();

  return useCallback(
    (error: unknown, handlers: AppErrorHandlers, options: IErrorHandleOptions = {}) => {
      const { toastId, redirect = currentRedirect() } = options;

      return runErrorStrategy(error, handlers, {
        notify: (content: string) => void addToast({ id: toastId, content, type: EToastType.ERROR }),
        goLogin: () => void navigate(EPath.Login, { replace: true, state: { redirect } }),
        networkMessage: t('fm.network_error')
      });
    },
    [addToast, navigate, t]
  );
};
